import React from "react";
import { useTranslation } from "react-i18next";
import { Grid } from "@mantine/core";
import Accordion from "@/components/atoms/accordions/DrawerAccordion";
import Text from "@/components/atoms/Text";
import Stack from "@/components/atoms/Stack";
import { Button } from "@mantine/core";
import SVGAsset from "@/components/atoms/SVGAsset/SVGAsset";

import { GroupOrderClientSide, Order } from "@/types";
import { useModal } from "@/contexts/ModalContext";

interface TransportTabProps {
  groupOrders: GroupOrderClientSide[];
  setGroupOrders: React.Dispatch<React.SetStateAction<GroupOrderClientSide[]>>;
  setOrders: React.Dispatch<React.SetStateAction<Order[]>>;
  initialOrders: Order[];
  onClose: () => void;
}

const TransportTab: React.FC<TransportTabProps> = (props) => {
  const { groupOrders, setGroupOrders, setOrders, initialOrders } = props;
  const { t } = useTranslation();
  const { openModal } = useModal();

  const handleConfirmDelete = (groupOrder: GroupOrderClientSide) => {
    openModal({
      title: t("tasks:deleteShipment"),
      content: t("tasks:confirmDeleteShipment"),
      type: "confirmation",
      primaryAction: {
        text: t("actions:accept"),
        onClick: () => {
          clearGrouped(groupOrder);
        },
        intent: "primary",
      },
      secondaryAction: {
        text: t("actions:cancel"),
        onClick: () => {},
        intent: "secondary",
      },
    });
  };

  const clearGrouped = (groupOrder: GroupOrderClientSide) => {
    try {
      const orderIds = groupOrder.groups.map((group) => group.orderId);

      const ordersToAdd = initialOrders.filter((order) => orderIds.includes(order.id));
      setOrders((prevOrders) => {
        const currentOrderIds = prevOrders.map((order) => order.id);
        const newOrdersToAdd = ordersToAdd.filter((order) => !currentOrderIds.includes(order.id));

        return [...prevOrders, ...newOrdersToAdd];
      });

      setGroupOrders((prevGroupOrders) => prevGroupOrders.filter((group) => group.groupName !== groupOrder.groupName));
    } catch (error) {
      console.error("Error clearing group:", error);
    }
  };

  if (groupOrders.length === 0) {
    return (
      <Text ta="center" my={20} size="sm" display="technical" tt="uppercase" strong={true}>
        {t("tasks:noLoads")}
      </Text>
    );
  }

  return (
    <Stack>
      {groupOrders.map((groupOrder, index) => (
        <Accordion key={`${index}-${groupOrder.groupName}`}>
          <Accordion.Item value={groupOrder.groupName.toString()}>
            <Grid align="center" justify="space-between">
              <Grid.Col span="auto" pl={10} mr={10}>
                <Accordion.Control>
                  <Text size="sm" display="technical" tt="uppercase" strong={true}>
                    {groupOrder.groupName}
                  </Text>
                </Accordion.Control>
              </Grid.Col>
              <Grid.Col span={1} pr={0}>
                <Button
                  variant="transparent"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleConfirmDelete(groupOrder);
                  }}
                  p={0}
                  title={t("actions:cancel")}
                >
                  <SVGAsset name="trashcan-dark" width="16" height="16" />
                </Button>
              </Grid.Col>
            </Grid>
            <Accordion.Panel>
              {groupOrder.groups.map((group, idx) => (
                <Grid key={`${idx}-${group.orderId}`} my={5}>
                  <Grid.Col span={2}>
                    <Text c="dimmed">#{group.orderId}</Text>
                  </Grid.Col>
                  <Grid.Col span="auto">
                    <Text>{group.groupName}</Text>
                  </Grid.Col>
                </Grid>
              ))}
            </Accordion.Panel>
          </Accordion.Item>
        </Accordion>
      ))}
    </Stack>
  );
};

export default TransportTab;
